import { useAppContext } from '../AppContext';
import { useCurrentUser } from '../providers/currentUser/CurrentUserProvider';
import { type PermissionId } from './permissions';

// Central access checks for the whole portal. Access is resolved from the current user's role permissions
// via hasPermission, where the access verb maps to a bit flag: read=1, create=2, update=4, delete=8.
//
// Super admins used to bypass every check, which meant anyone in the TouchTab org held full access with no
// roles assigned at all — membership of the org was the grant. They are now subject to their permissions
// like everyone else, so a super admin's access comes from the roles they hold. `isSuperAdmin` is still
// returned, but only for the surfaces that are *restricted* to super admins; it no longer grants anything.
export function usePermissions() {
    const currentUser = useCurrentUser();
    const app = useAppContext();
    const isSuperAdmin = app.isSuperAdmin();

    const check = (verb: 'read' | 'create' | 'update' | 'delete', id: PermissionId) =>
        currentUser.hasPermission(`${verb}:${id}`);

    return {
        isSuperAdmin,
        canRead: (id: PermissionId) => check('read', id),
        canCreate: (id: PermissionId) => check('create', id),
        canUpdate: (id: PermissionId) => check('update', id),
        canDelete: (id: PermissionId) => check('delete', id),
    };
}
